import { defineStore } from "pinia"
import { fetchReleaseNotes, isShowable, markReleaseNotesSeen } from "@/services/releaseNotes"
import { logger } from "@/services/logger"
import { mayRequestInBackground } from "@/services/backgroundRequests"

/**
 * Bumped by every markSeen. An answer to a request sent before the latest acknowledgement predates it,
 * and applying it would bring back as news what was just dismissed — the help button's dot, or the
 * changelog's "Neu" marks after arriving from the dialog, depending on which request won the race.
 */
let writeGeneration = 0

/**
 * The changelog ("Neuigkeiten") and which of it the signed-in user has already been shown.
 *
 * Loaded once per session rather than polled like the bell: a release only arrives with a
 * deployment, and a deployment reloads the SPA anyway (services/versionCheck).
 */
export const useReleaseNotesStore = defineStore("releaseNotes", {
  state: () => ({
    releases: [],
    loaded: false,
  }),
  getters: {
    /**
     * Releases that should open the dialog: unseen, still inside their announce window, and with
     * wording this client can show (see isShowable).
     */
    announced: state => state.releases.filter(release => release.announce && isShowable(release)),
    hasNews() {
      return this.announced.length > 0
    },
  },
  actions: {
    /** See services/backgroundRequests.js — shared with the bell. */
    canLoad() {
      return mayRequestInBackground()
    },

    async load() {
      if (!this.canLoad()) return
      const generation = writeGeneration
      try {
        const response = await fetchReleaseNotes()
        if (generation === writeGeneration) this.releases = response.data?.releases ?? []
        this.loaded = true
      } catch (error) {
        // Release notes are never worth breaking a page over; the interceptor surfaces server errors.
        logger.error(`Failed to load release notes: ${error}`)
      }
    },

    /**
     * Marks [ids] seen; an empty list marks everything visible. Optimistic, like completeOnboarding:
     * the dialog must not be able to come back because a request is still in flight, and a failed
     * write only costs one repeat of the dialog next session.
     */
    async markSeen(ids = []) {
      if (!this.canLoad()) return
      const generation = ++writeGeneration
      const acknowledged = ids.length ? new Set(ids) : null
      this.releases = this.releases.map(release =>
        !acknowledged || acknowledged.has(release.id) ? { ...release, seen: true, announce: false } : release
      )
      try {
        const response = await markReleaseNotesSeen(ids)
        if (generation === writeGeneration) this.releases = response.data?.releases ?? this.releases
      } catch (error) {
        logger.error(`Failed to mark release notes as seen: ${error}`)
      }
    },

    reset() {
      this.releases = []
      this.loaded = false
    },
  },
})
